import { useState } from "react";
import { ShoppingCart, Leaf, Star, Zap, Circle, Sun, Moon, Package, Menu, X, ChevronRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useMushrooms } from "../context/MushroomsContext";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import { useLang } from "../context/LanguageContext";
import { DropdownNavigation } from "./ui/dropdown-navigation";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "../context/ThemeContext";

const CATEGORY_CONFIG = {
  everyday:  { title: "Everyday Picks", icon: Circle },
  premium:   { title: "Premium",        icon: Star   },
  exotic:    { title: "Specialty",      icon: Zap    },
  medicinal: { title: "Specialty",      icon: Zap    },
};

function buildShopSubMenus(mushrooms) {
  const groups = {};
  mushrooms
    .filter(m => m.isActive !== false)
    .forEach(m => {
      const cfg = CATEGORY_CONFIG[m.category] || { title: "Other", icon: Circle };
      if (!groups[cfg.title]) groups[cfg.title] = { title: cfg.title, icon: cfg.icon, items: [] };
      groups[cfg.title].items.push({
        label: m.name,
        description: m.description?.slice(0, 40) + "…",
        icon: groups[cfg.title].icon,
        link: `/mushroom/${m.id}`,
      });
    });
  return Object.values(groups);
}

export default function Navbar() {
  const { cartCount, setIsCartOpen } = useCart();
  const { isDark, toggle } = useTheme();
  const { mushrooms } = useMushrooms();
  const { customer } = useCustomerAuth();
  const { lang, toggle: toggleLang, t } = useLang();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileShopOpen, setMobileShopOpen] = useState(false);

  const navItems = [
    { id: 1, label: t('nav.shopNow'),      subMenus: buildShopSubMenus(mushrooms) },
    { id: 2, label: t('nav.allVarieties'), link: "/shop"      },
    { id: 3, label: t('nav.recipes'),      link: "/#recipes"  },
    { id: 4, label: t('nav.podcasts'),     link: "/#podcasts" },
    { id: 5, label: t('nav.about'),        link: "/#about"    },
  ];

  const closeMobile = () => { setMobileOpen(false); setMobileShopOpen(false); };

  const handleMobileLink = (href) => {
    closeMobile();
    if (href.startsWith("/#")) {
      const hash = href.slice(2);
      if (window.location.pathname !== "/") {
        navigate("/");
        setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" }), 200);
      } else {
        document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(href);
    }
  };

  const activeMushrooms = mushrooms.filter(m => m.isActive !== false);

  return (
    <>
      <motion.nav
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="fixed top-0 left-0 right-0 z-50 border-b"
        style={{ backgroundColor: 'var(--c-bg-blur)', backdropFilter: 'blur(16px)', borderColor: 'var(--c-border)' }}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group" data-testid="navbar-logo">
            <div className="w-8 h-8 rounded-lg overflow-hidden ring-1 group-hover:ring-[#D97A46]/50 transition-all">
              <img src="https://images.unsplash.com/photo-1770884844724-ac9e36b599e9?auto=format&fit=crop&w=64&q=80" alt="PIIKK" className="w-full h-full object-cover" />
            </div>
            <div>
              <span className="font-serif text-xl font-bold tracking-tight leading-none" style={{ color: 'var(--c-text)' }}>PIIKK</span>
              <span className="block text-[10px] font-medium tracking-widest uppercase leading-none" style={{ color: '#D97A46' }}>Agro Farm Fresh</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex">
            <DropdownNavigation navItems={navItems} />
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-2">
            {/* Desktop only pills */}
            <Link to="/shop"
              className="hidden md:flex items-center gap-1.5 px-4 py-2 rounded-full border text-sm font-medium transition-all hover:-translate-y-0.5"
              style={{ borderColor: 'var(--c-border)', color: 'var(--c-text)', backgroundColor: 'var(--c-surface)' }}
              data-testid="navbar-shop-link">
              <Leaf className="w-3.5 h-3.5" style={{ color: '#D97A46' }} /> {t('nav.shopNow')}
            </Link>

            {customer && (
              <Link to="/my-orders"
                className="hidden md:flex items-center gap-1.5 px-4 py-2 rounded-full border text-sm font-medium transition-all hover:-translate-y-0.5"
                style={{ borderColor: 'var(--c-border)', color: 'var(--c-text)', backgroundColor: 'var(--c-surface)' }}
                data-testid="my-orders-nav-link">
                <Package className="w-3.5 h-3.5" style={{ color: '#D97A46' }} /> {t('nav.myOrders')}
              </Link>
            )}

            {/* Language toggle */}
            <button
              data-testid="lang-toggle-btn"
              onClick={toggleLang}
              aria-label="Switch language"
              className="px-2 sm:px-3 py-2 rounded-full border text-xs font-bold transition-all hover:-translate-y-0.5"
              style={{ borderColor: 'var(--c-border)', backgroundColor: 'var(--c-surface)', color: lang === 'hi' ? '#D97A46' : 'var(--c-muted)' }}
            >
              {lang === 'en' ? 'हिं' : 'EN'}
            </button>

            {/* Theme toggle — always visible */}
            <button
              data-testid="theme-toggle-btn"
              onClick={toggle}
              aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
              className="p-2.5 rounded-full border transition-all hover:-translate-y-0.5"
              style={{ borderColor: 'var(--c-border)', backgroundColor: 'var(--c-surface)', color: 'var(--c-muted)' }}
            >
              {isDark
                ? <Sun className="w-4 h-4" style={{ color: '#D97A46' }} />
                : <Moon className="w-4 h-4" />
              }
            </button>

            {/* Cart — always visible */}
            <button data-testid="navbar-cart-button" onClick={() => setIsCartOpen(true)}
              className="relative p-2.5 rounded-full border transition-all hover:-translate-y-0.5"
              style={{ borderColor: 'var(--c-border)', backgroundColor: 'var(--c-surface)', color: 'var(--c-text)' }}>
              <ShoppingCart className="w-5 h-5" />
              <AnimatePresence>
                {cartCount > 0 && (
                  <motion.span key="badge" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}
                    className="absolute -top-1 -right-1 w-5 h-5 text-white text-[10px] font-bold rounded-full flex items-center justify-center"
                    style={{ backgroundColor: '#D97A46' }} data-testid="cart-count-badge">
                    {cartCount}
                  </motion.span>
                )}
              </AnimatePresence>
            </button>

            {/* Hamburger — mobile/tablet only */}
            <button
              className="md:hidden p-2.5 rounded-full border transition-all"
              style={{ borderColor: 'var(--c-border)', backgroundColor: 'var(--c-surface)', color: 'var(--c-text)' }}
              onClick={() => setMobileOpen(o => !o)}
              aria-label="Open menu"
              data-testid="mobile-menu-btn"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu Drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/40 z-40 md:hidden"
              onClick={closeMobile}
            />
            {/* Panel */}
            <motion.div
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="fixed top-0 right-0 bottom-0 w-[280px] z-50 flex flex-col md:hidden border-l"
              style={{ backgroundColor: 'var(--c-surface)', borderColor: 'var(--c-border)' }}
              data-testid="mobile-menu"
            >
              {/* Panel header */}
              <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--c-border)' }}>
                <span className="font-serif text-base font-bold" style={{ color: 'var(--c-text)' }}>{t('nav.menu')}</span>
                <button onClick={closeMobile} className="p-1.5 rounded-full" style={{ color: 'var(--c-muted)' }}>
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto py-3 px-4 space-y-1">
                {/* Shop Now CTA */}
                <button onClick={() => handleMobileLink("/shop")}
                  className="w-full flex items-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm text-white transition-all"
                  style={{ backgroundColor: '#D97A46' }} data-testid="mobile-shop-now-btn">
                  <Leaf className="w-4 h-4" /> {t('nav.shopNow')}
                </button>

                {customer && (
                  <button onClick={() => handleMobileLink("/my-orders")}
                    className="w-full flex items-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm transition-all border"
                    style={{ backgroundColor: 'var(--c-bg)', borderColor: 'var(--c-border)', color: 'var(--c-text)' }}
                    data-testid="mobile-my-orders-btn">
                    <Package className="w-4 h-4" style={{ color: '#D97A46' }} /> {t('nav.myOrders')}
                  </button>
                )}

                {/* Language toggle in mobile */}
                <button onClick={toggleLang}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium transition-colors"
                  style={{ color: 'var(--c-text)', backgroundColor: 'var(--c-bg)' }}
                  data-testid="mobile-lang-toggle">
                  <span>{lang === 'en' ? '🇮🇳 हिंदी में देखें' : '🇬🇧 Switch to English'}</span>
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(217,122,70,0.15)', color: '#D97A46' }}>
                    {lang === 'en' ? 'हिं' : 'EN'}
                  </span>
                </button>

                <div className="h-px my-2" style={{ backgroundColor: 'var(--c-border)' }} />

                {/* Shop submenu toggle */}
                <button
                  onClick={() => setMobileShopOpen(o => !o)}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium transition-colors hover:opacity-80"
                  style={{ color: 'var(--c-text)', backgroundColor: 'var(--c-bg)' }}
                  data-testid="mobile-shop-submenu-toggle"
                >
                  <span className="flex items-center gap-2"><Circle className="w-4 h-4" style={{ color: '#D97A46' }} /> {t('nav.shopByVariety')}</span>
                  <ChevronRight className={`w-4 h-4 transition-transform ${mobileShopOpen ? "rotate-90" : ""}`} style={{ color: 'var(--c-muted)' }} />
                </button>

                <AnimatePresence>
                  {mobileShopOpen && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden pl-3 space-y-1">
                      {activeMushrooms.map(m => (
                        <button key={m.id} onClick={() => handleMobileLink(`/mushroom/${m.id}`)}
                          className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-left transition-colors hover:opacity-80"
                          style={{ color: 'var(--c-muted)' }}>
                          <ChevronRight className="w-3 h-3 flex-shrink-0" style={{ color: '#D97A46' }} />
                          {m.name}
                        </button>
                      ))}
                      <button onClick={() => handleMobileLink("/shop")}
                        className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-semibold transition-colors"
                        style={{ color: '#D97A46' }}>
                        {t('nav.viewAll')}
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Static links */}
                {[
                  { label: t('nav.allVarieties'), href: "/shop" },
                  { label: t('nav.recipes'),      href: "/#recipes"  },
                  { label: t('nav.podcasts'),     href: "/#podcasts" },
                  { label: t('nav.about'),        href: "/#about"    },
                ].map(({ label, href }) => (
                  <button key={href} onClick={() => handleMobileLink(href)}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium transition-colors hover:opacity-80"
                    style={{ color: 'var(--c-text)' }}>
                    {label}
                    <ChevronRight className="w-4 h-4" style={{ color: 'var(--c-muted)' }} />
                  </button>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
